// Offline Havana — localStorage-backed cache for businesses + favorites + journey.
// Cheap and reliable for a v1 PWA; can be migrated to IndexedDB later.
import { api } from "./api";

const K = {
  manifest: "habano.offline.manifest",
  businesses: "habano.offline.businesses",
  favorites: "habano.offline.favorites",
  visits: "habano.offline.visits",
  vendor_meets: "habano.offline.vendor_meets",
};

const ls = typeof window !== "undefined" ? window.localStorage : null;

const safeRead = (k) => {
  try { return ls ? JSON.parse(ls.getItem(k) || "null") : null; } catch { return null; }
};
const safeWrite = (k, v) => { try { ls && ls.setItem(k, JSON.stringify(v)); } catch {} };
const safeClear = (k) => { try { ls && ls.removeItem(k); } catch {} };

export function getManifest() {
  return safeRead(K.manifest);
}

export function getCachedBusinesses() {
  return safeRead(K.businesses) || [];
}

export function getCachedBusiness(id) {
  return getCachedBusinesses().find((b) => b.id === id) || null;
}

export function getCachedFavorites() {
  return safeRead(K.favorites) || [];
}

export function getCachedVisits() {
  return safeRead(K.visits) || [];
}

export async function downloadOfflinePack() {
  const out = { startedAt: new Date().toISOString() };
  const [biz, favs, visits, vendorMeets] = await Promise.all([
    api.get("/businesses?limit=1000").then((r) => r.data).catch(() => []),
    api.get("/journey/favorites").then((r) => r.data).catch(() => []),
    api.get("/journey/visits").then((r) => r.data).catch(() => []),
    api.get("/journey/vendor-meets").then((r) => r.data).catch(() => []),
  ]);
  safeWrite(K.businesses, biz);
  safeWrite(K.favorites, favs);
  safeWrite(K.visits, visits);
  safeWrite(K.vendor_meets, vendorMeets);

  // Pre-warm Mapbox tiles for Havana bbox at z=12..14 via the service worker.
  const tilesQueued = prewarmMapboxTiles();

  const manifest = {
    downloaded: true,
    updated_at: new Date().toISOString(),
    counts: { businesses: biz.length, favorites: favs.length, visits: visits.length, vendor_meets: vendorMeets.length },
    bytes: estimateSize(),
    mapbox_tiles: process.env.REACT_APP_MAPBOX_TOKEN
      ? (tilesQueued > 0 ? "active" : "queued")
      : "pending_token",
    mapbox_tiles_queued: tilesQueued,
  };
  safeWrite(K.manifest, manifest);
  out.manifest = manifest;
  return manifest;
}

// iter118 — Compute the Mapbox tile URL set covering the Havana bbox at
// zoom levels 10..15 (per Save-for-Offline spec) and dispatch them to
// the service worker for cache pre-warming. Retina (@2x) variants are
// fetched for high-DPI devices unless the user is on 2G/3G — see the
// effective-connection gate below.
function prewarmMapboxTiles() {
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token || typeof navigator === "undefined" || !navigator.serviceWorker) return 0;
  // Wider Havana bbox per spec (covers Old Havana → Vedado → Playa east of Almendares).
  const bbox = { west: -82.48, east: -82.28, south: 23.05, north: 23.20 };
  const lon2tile = (lon, z) => Math.floor(((lon + 180) / 360) * Math.pow(2, z));
  const lat2tile = (lat, z) => {
    const rad = (lat * Math.PI) / 180;
    return Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * Math.pow(2, z));
  };
  // Skip @2x retina tiles on slow connections to keep the download
  // sub-2-minutes on Cuban networks. Network Information API isn't
  // exposed in Safari yet, so missing values are treated as "fast".
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const effective = conn?.effectiveType || "4g";
  const slow = effective === "slow-2g" || effective === "2g" || effective === "3g";
  const retina = !slow && typeof window !== "undefined" && (window.devicePixelRatio || 1) >= 1.5;

  const urls = [];
  // Style + sprite + glyphs (essential for first paint)
  urls.push(`https://api.mapbox.com/styles/v1/mapbox/dark-v11?access_token=${token}`);
  urls.push(`https://api.mapbox.com/styles/v1/mapbox/dark-v11/sprite.json?access_token=${token}`);
  urls.push(`https://api.mapbox.com/styles/v1/mapbox/dark-v11/sprite.png?access_token=${token}`);
  // Raster tiles for visible Havana bbox across zooms 10..15.
  for (let z = 10; z <= 15; z++) {
    const xMin = lon2tile(bbox.west, z);
    const xMax = lon2tile(bbox.east, z);
    const yMin = lat2tile(bbox.north, z);
    const yMax = lat2tile(bbox.south, z);
    for (let x = xMin; x <= xMax; x++) {
      for (let y = yMin; y <= yMax; y++) {
        urls.push(`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/256/${z}/${x}/${y}?access_token=${token}`);
        if (retina) {
          urls.push(`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/256/${z}/${x}/${y}@2x?access_token=${token}`);
        }
      }
    }
  }
  navigator.serviceWorker.ready
    .then((reg) => reg.active && reg.active.postMessage({ type: "PREWARM_MAPBOX", urls }))
    .catch(() => {});
  return urls.length;
}

export function removeOfflinePack() {
  Object.values(K).forEach(safeClear);
}

function estimateSize() {
  let total = 0;
  Object.values(K).forEach((k) => {
    const v = ls?.getItem(k);
    if (v) total += v.length;
  });
  return total; // approx bytes — localStorage stores as UTF-16 so real size ~2x
}

export function fmtBytes(n) {
  if (!n) return "0 KB";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

// Subscribe to online/offline transitions
export function subscribeNetwork(cb) {
  const fire = () => cb(isOnline());
  window.addEventListener("online", fire);
  window.addEventListener("offline", fire);
  fire();
  return () => {
    window.removeEventListener("online", fire);
    window.removeEventListener("offline", fire);
  };
}
